import { db } from '../../../integrations/firebase';
import { assigneeOnCreate, takesOwnership } from '../logic/assignment';
import { ref, push, set, get, update, remove, serverTimestamp } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { NotificationService } from '../../notifications/services/NotificationService';
import { authHeaders } from '../../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
import { categoryForIssue } from '../constants';
import { inferPriority, departmentForIssue, computeDueAt } from '../logic/priority';
import { getSupportSettings, ensureSupportSettings, autoAssigneeFor, effectiveStart, formatTicketNumber } from '../logic/supportSettings';
import { recordTimelineEvent } from '../../../core/customer/CustomerTimelineEngine';

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null));

// --- Customer timeline integration (section 4) ---
// Routes through the unified CustomerTimelineEngine (single source of truth).
const addCustomerTimeline = async (mobile, entry) => {
    const { type, createdBy, ...rest } = entry || {};
    await recordTimelineEvent(mobile, type || 'complaint_ticket', rest, createdBy || 'system');
};

export const TicketService = {

    // Delete a conversation entry (internal note / customer reply / inbound message).
    deleteConversationEntry: async (ticketId, kind, entryId) => {
        if (!ticketId || !entryId) throw new Error('Missing id.');
        const path = kind === 'internal' ? 'internalNotes' : kind === 'inbound' ? 'customerMessages' : 'publicUpdates';
        await remove(ref(db, `tickets/${ticketId}/${path}/${entryId}`));
        await update(ref(db, `tickets/${ticketId}`), { updatedAt: Date.now() });
    },


    // Transcribe (Malayalam/regional, speaker-labelled) + English summary of a
    // ticket audio attachment, via the Sarvam-backed proxy. Result is cached on
    // the ticket under transcripts/{hash} so repeat opens don't re-cost.
    // AI case analysis (what happened / story so far / risks / next action) from a
    // text digest of the ticket. Cached on the ticket; regenerates when the case changes.
    analyzeCase: async (ticketId, digest, { force = false } = {}) => {
        if (!PROXY_BASE) throw new Error('AI service not configured.');
        const r = await fetch(`${PROXY_BASE}/api/tickets/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ ticketId, digest, force }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Analysis failed (${r.status})`);
        return j;
    },

    // --- Email channel (Freshdesk-style) ---
    // Whether the proxy has inbound (IMAP) / outbound (SMTP) email wired.
    emailStatus: async () => {
        if (!PROXY_BASE) return { inbound: false, outbound: false, address: '' };
        try {
            const r = await fetch(`${PROXY_BASE}/api/email/status`, { headers: { ...(await authHeaders()) } });
            if (!r.ok) return { inbound: false, outbound: false, address: '' };
            return await r.json();
        } catch { return { inbound: false, outbound: false, address: '' }; }
    },
    // Admin: pull the mailbox right now instead of waiting for the cron.
    pollEmailNow: async () => {
        if (!PROXY_BASE) throw new Error('Email service not configured.');
        const r = await fetch(`${PROXY_BASE}/api/email/poll-now`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeaders()) } });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Poll failed (${r.status})`);
        return j;
    },
    // Send an agent reply out as an email (SMTP) on an email-sourced ticket. The
    // ticket token stays in the subject so the customer's reply threads back.
    sendEmailReply: async (ticketId, { to, subject, text, html, inReplyTo, references } = {}) => {
        if (!PROXY_BASE) throw new Error('Email sending not configured.');
        const r = await fetch(`${PROXY_BASE}/api/tickets/email-reply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ ticketId, to, subject, text, html, inReplyTo, references }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Email send failed (${r.status})`);
        return j;
    },

    transcribeAudio: async (ticketId, audioUrl, { force = false } = {}) => {
        if (!PROXY_BASE) throw new Error('Transcription service not configured.');
        const r = await fetch(`${PROXY_BASE}/api/tickets/transcribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ ticketId, audioUrl, force }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Transcription failed (${r.status})`);
        return j;
    },

    nextTicketNumber: async () => {
        const seqRef = ref(db, 'system_sequences/ticket_sequence');
        const snap = await get(seqRef);
        const next = (snap.exists() ? (snap.val() || 0) : 0) + 1;
        await set(seqRef, next);
        return { num: next, ticketNumber: formatTicketNumber(next, await ensureSupportSettings()) };
    },

    createTicket: async (payload, currentUser, { isRepeatCustomer = false } = {}) => {
        const { ticketNumber } = await TicketService.nextTicketNumber();
        const now = Date.now();
        const issueType = payload.issueType || 'Other Complaint';
        const priority = payload.priority || inferPriority(issueType, { isRepeatCustomer });
        const category = payload.category || categoryForIssue(issueType);
        const assignedDepartment = payload.assignedDepartment || departmentForIssue(issueType);
        // Support settings: auto-assignment rule (issue type → dept fallback) and
        // the weekend rule (Sat-evening/Sunday complaints start the clock Monday).
        const settings = getSupportSettings();
        // Who ends up owning this — see logic/assignment.js for the two rules.
        const creatorUid = currentUser?.uid || currentUser?.email || '';
        // Held in a variable because the audit log below needs it too. It used to be
        // written inline here and then read again after the ticket was saved, which
        // threw "autoRule is not defined" — AFTER the write, so the ticket existed but
        // the timeline, escalation link and notifications that follow never ran.
        const autoRule = payload.assignedTo ? null : autoAssigneeFor(issueType, assignedDepartment, settings);
        const owner = assigneeOnCreate({
            explicitUid: payload.assignedTo || '',
            explicitName: payload.assignedToName || '',
            autoRule,
            creatorUid,
        });
        const clockStart = effectiveStart(now, settings);

        const ticketRef = push(ref(db, 'tickets'));
        const ticket = clean({
            ticketNumber,
            customerId: payload.customerId || '',
            customerName: payload.customerName || '',
            mobile: payload.mobile || '',
            alternateMobile: payload.alternateMobile || '',
            orderId: payload.orderId || '',
            batchNo: payload.batchNo || '',
            productIds: payload.productIds || [],
            productNames: payload.productNames || [],
            issueType,
            category,
            priority,
            status: payload.status || 'open',
            assignedTo: owner.assignedTo,
            assignedToName: owner.assignedToName,
            assignedDepartment,
            source: payload.source || 'manual',
            description: payload.description || '',
            internalNotes: [],
            publicUpdates: [],
            attachments: payload.attachments || [],
            createdBy: currentUser?.uid || currentUser?.email || 'system',
            createdAt: now,
            updatedAt: now,
            dueAt: computeDueAt(priority, clockStart, issueType),
            slaClockStart: clockStart,
            closedAt: '',
            slaBreached: false,
            mergedInto: '',
            relatedTickets: [],
            escalationLevel: 0,
            // import metadata (section 19)
            importedSource: payload.importedSource || '',
            importedStatus: payload.importedStatus || '',
            issueCode: payload.issueCode || '',
        });
        await set(ticketRef, ticket);
        // Only when the rule actually WON. assigneeOnCreate drops a rule that points at
        // the agent raising the ticket, and logging "assigned" for an assignment that
        // did not happen would make the audit trail disagree with the ticket.
        if (autoRule?.uid && owner.assignedTo === autoRule.uid) {
            await TicketService.addAuditLog(ticketRef.key, { action: 'assigned', previousValue: 'Unassigned', newValue: autoRule.name || autoRule.uid, performedBy: 'auto-rule', note: 'Automatic assignment rule' });
        }

        await addCustomerTimeline(payload.mobile, {
            type: 'complaint_ticket', ticketId: ticketRef.key, ticketNumber,
            issueType, status: ticket.status, note: payload.description || '',
            createdBy: ticket.createdBy,
        });

        // Complaint ↔ Courier Escalation linking (Section 16). Any delivery/courier
        // complaint funnels into the single courier escalation for that order.
        if (category === categoryForIssue(issueType) && categoryForIssue(issueType) === 'delivery' && ticket.orderId) {
            try {
                const { CourierEscalationService } = await import('../../escalations/services/CourierEscalationService');
                await CourierEscalationService.createOrAttach(
                    { id: ticket.orderId, customer: { name: ticket.customerName, mobile: ticket.mobile }, payment: {} },
                    { issueType, source: 'complaint', ticketId: ticketRef.key, note: payload.description || '', vip: isRepeatCustomer },
                    currentUser,
                );
            } catch (e) { console.warn('[Escalation] auto-link failed:', e.message); }
        }

        // Optional follow-up → To-Do (section 9)
        if (payload.followUpAt) {
            await TicketService.createFollowUp(
                { id: ticketRef.key, ticketNumber, orderId: ticket.orderId, customerName: ticket.customerName, mobile: ticket.mobile },
                { followUpAt: payload.followUpAt, assignedTo: ticket.assignedTo, reason: 'Initial follow-up' },
                currentUser
            );
        }

        return { id: ticketRef.key, ticketNumber, ...ticket };
    },

    updateTicket: async (id, updates) =>
        update(ref(db, `tickets/${id}`), clean({ ...updates, updatedAt: Date.now() })),

    // Assign / reassign a ticket to a specific agent (stores both uid + a stable
    // display name so lists never fall back to a raw uid), fully audited.
    assignAgent: async (ticket, uid, name, currentUser) => {
        await update(ref(db, `tickets/${ticket.id}`), clean({
            assignedTo: uid || '',
            assignedToName: name || '',
            updatedAt: Date.now(),
        }));
        await TicketService.addAuditLog(ticket.id, {
            action: 'assigned',
            previousValue: ticket.assignedToName || ticket.assignedTo || 'Unassigned',
            newValue: name || uid || 'Unassigned',
            performedBy: currentUser?.uid || currentUser?.email || 'system',
        });
    },

    /**
     * @returns {{assignedToMe: boolean}} so the caller can tell the agent what happened.
     *
     * Moving a complaint to PENDING means "I am working on this", so it is assigned to
     * whoever made that change — reassigning it away from a previous owner if needed.
     * Any other status leaves the assignment alone.
     */
    changeStatus: async (id, status, currentUser, { ticketNumber, mobile, issueType, assignedTo } = {}, { lateReason } = {}) => {
        const me = currentUser?.uid || '';
        const myName = currentUser?.displayName || currentUser?.name || currentUser?.email || '';
        const takeOwnership = takesOwnership(status, me, assignedTo || '');
        const updates = clean({
            status, updatedAt: Date.now(),
            ...(takeOwnership ? { assignedTo: me, assignedToName: myName } : {}),
            ...(status === 'closed' || status === 'resolved' ? {
                closedAt: Date.now(),
                closedBy: currentUser?.uid || '',
                closedByName: currentUser?.displayName || currentUser?.name || currentUser?.email || '',
                ...(lateReason ? { lateCloseReason: lateReason } : {}),
            } : {}),
        });
        await update(ref(db, `tickets/${id}`), updates);
        if (lateReason) {
            await TicketService.addAuditLog(id, {
                action: 'closed_late', newValue: lateReason,
                performedBy: currentUser?.displayName || currentUser?.name || currentUser?.email || 'agent',
                note: 'Closed after the SLA time limit',
            });
        }
        if (takeOwnership) {
            await TicketService.addAuditLog(id, {
                action: 'assigned',
                previousValue: assignedTo || 'Unassigned',
                newValue: myName || me,
                performedBy: myName || me,
                note: 'Auto-assigned on moving the complaint to Pending',
            });
        }
        await addCustomerTimeline(mobile, {
            type: 'complaint_ticket', ticketId: id, ticketNumber, issueType, status,
            note: `Status changed to ${status}`, createdBy: currentUser?.uid || 'system',
        });
        return { assignedToMe: takeOwnership };
    },

    // Upload a file to Storage for a ticket → returns attachment metadata.
    uploadAttachment: async (ticketId, file) => {
        const storage = getStorage();
        const safe = String(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
        const fileRef = storageRef(storage, `ticket-attachments/${ticketId}/${Date.now()}_${safe}`);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        return { name: file.name, url, size: file.size, type: file.type, uploadedAt: Date.now() };
    },

    // Upload a shared asset (image / voice) for canned responses.
    uploadCannedAsset: async (file) => {
        const storage = getStorage();
        const safe = String(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
        const fileRef = storageRef(storage, `canned-responses/${Date.now()}_${safe}`);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        return { name: file.name, url, size: file.size, type: file.type, uploadedAt: Date.now() };
    },

    addInternalNote: async (id, message, currentUser, department = 'support', attachments = [], mentions = [], { messageHtml } = {}) => {
        const noteRef = push(ref(db, `tickets/${id}/internalNotes`));
        await set(noteRef, clean({
            message, messageHtml: messageHtml || undefined, department, visibility: 'internal',
            attachments: (attachments && attachments.length) ? attachments : undefined,
            mentions: (mentions && mentions.length) ? mentions.map(m => ({ uid: m.uid, name: m.name })) : undefined,
            createdBy: currentUser?.displayName || currentUser?.email || 'agent',
            createdAt: Date.now(),
        }));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },

    // Ping a mentioned teammate: in-app notification (always) + email (best-effort,
    // dormant until the proxy has an SMTP provider configured).
    pingMention: async (mention, { author, ticketId, ticketNumber, message }) => {
        if (!mention?.uid) return;
        const snippet = String(message || '').slice(0, 140);
        try {
            await NotificationService.send(mention.uid, {
                title: `${author} mentioned you`,
                message: `${ticketNumber || 'Ticket'}: ${snippet}`,
                type: 'ticket_mention',
                ticketId: ticketId || '',
                ticketNumber: ticketNumber || '',
                link: '/complaints',
            });
        } catch (e) { console.warn('[Mention] in-app notify failed:', e.message); }

        if (mention.email && PROXY_BASE) {
            try {
                await fetch(`${PROXY_BASE}/api/notify/email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
                    body: JSON.stringify({
                        to: mention.email,
                        subject: `${author} mentioned you on ${ticketNumber || 'a ticket'}`,
                        text: `${author} mentioned you on ticket ${ticketNumber || ''}:\n\n"${message || ''}"\n\nOpen the ERP to reply.`,
                    }),
                });
            } catch (e) { /* email is best-effort; in-app already delivered */ }
        }
    },

    // Freshdesk-style public reply to the customer (recorded in the thread with
    // optional attachments). Outbound WhatsApp/SMS delivery is handled by the
    // existing notification path; this records the conversation entry.
    addCustomerReply: async (id, message, currentUser, { channel = 'whatsapp', attachments = [], messageHtml } = {}) => {
        const upRef = push(ref(db, `tickets/${id}/publicUpdates`));
        await set(upRef, clean({
            message, messageHtml: messageHtml || undefined, channel, sentToCustomer: true, status: 'sent', visibility: 'public',
            attachments: (attachments && attachments.length) ? attachments : undefined,
            createdBy: currentUser?.displayName || currentUser?.email || 'agent',
            createdAt: Date.now(),
        }));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },

    addPublicUpdate: async (id, message, { status, channel = 'whatsapp', sentToCustomer = false, messageHtml } = {}) => {
        const upRef = push(ref(db, `tickets/${id}/publicUpdates`));
        await set(upRef, clean({ message, messageHtml: messageHtml || undefined, status, channel, sentToCustomer, createdAt: Date.now() }));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },

    // Department escalation (section 17)
    escalate: async (id, { fromDepartment, toDepartment, assignedTo, reason }, currentUser, meta = {}) => {
        const escRef = push(ref(db, `tickets/${id}/escalationHistory`));
        await set(escRef, clean({
            fromDepartment, toDepartment, assignedTo, reason,
            createdBy: currentUser?.uid || 'system', createdAt: Date.now(),
        }));
        const statusByDept = { doctor: 'doctor_review', courier: 'courier_escalation', accounts: 'refund_pending' };
        await update(ref(db, `tickets/${id}`), clean({
            assignedDepartment: toDepartment,
            assignedTo: assignedTo || '',
            status: statusByDept[toDepartment] || undefined,
            updatedAt: Date.now(),
        }));
        await addCustomerTimeline(meta.mobile, {
            type: 'complaint_ticket', ticketId: id, ticketNumber: meta.ticketNumber,
            status: statusByDept[toDepartment] || 'escalated',
            note: `Escalated to ${toDepartment}`, createdBy: currentUser?.uid || 'system',
        });
    },

    // Follow-up → To-Do (section 9)
    createFollowUp: async (ticket, { followUpAt, assignedTo, reason }, currentUser) => {
        const taskRef = push(ref(db, 'tasks'));
        await set(taskRef, clean({
            id: taskRef.key,
            title: `Follow up ticket ${ticket.ticketNumber}`,
            ticketId: ticket.id,
            orderId: ticket.orderId || '',
            customerName: ticket.customerName || '',
            phone: ticket.mobile || '',
            dueAt: followUpAt,
            assignedTo: assignedTo || '',
            type: 'complaint_followup',
            status: 'pending',
            reason: reason || '',
            createdBy: currentUser?.uid || 'system',
            createdAt: Date.now(),
        }));
        // Mirror onto the ticket
        const fuRef = push(ref(db, `tickets/${ticket.id}/followUps`));
        await set(fuRef, clean({
            followUpAt, assignedTo, reason, status: 'pending',
            taskId: taskRef.key, createdBy: currentUser?.uid || 'system', createdAt: Date.now(),
        }));
        return taskRef.key;
    },

    // Doctor case — FIELD-LEVEL merge into tickets/{id}/doctorCase (never
    // overwrites the whole ticket). Section 7.
    updateDoctorCase: async (id, fields) => {
        await update(ref(db, `tickets/${id}/doctorCase`), clean(fields));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },

    // Doctor suggests discontinue + optional follow-up after N days.
    setDoctorAdvice: async (ticket, { doctorAdvice, discontinueSuggested, followUpAfterDays, doctorAssigned }, currentUser) => {
        const fields = clean({ doctorAdvice, discontinueSuggested, followUpAfterDays, doctorAssigned, status: 'advice_given' });
        await TicketService.updateDoctorCase(ticket.id, fields);
        if (currentUser) {
            await TicketService.addInternalNote(ticket.id, `Doctor advice: ${doctorAdvice || '(see case)'}${discontinueSuggested ? ' — discontinue product' : ''}`, currentUser, 'doctor');
        }
        if (followUpAfterDays && Number(followUpAfterDays) > 0) {
            const followUpAt = Date.now() + Number(followUpAfterDays) * 24 * 60 * 60 * 1000;
            await TicketService.updateDoctorCase(ticket.id, { followUpAt, status: 'followup_scheduled' });
            await TicketService.createFollowUp(ticket, { followUpAt, assignedTo: doctorAssigned || ticket.assignedTo, reason: 'Doctor follow-up' }, currentUser);
        }
    },

    // Refund / Replacement / Return — FIELD-LEVEL merges into their sub-objects.
    // Never overwrite the whole ticket or any order object.
    updateRefundCase: async (id, fields) => {
        await update(ref(db, `tickets/${id}/refundCase`), clean(fields));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },
    updateReplacementCase: async (id, fields) => {
        await update(ref(db, `tickets/${id}/replacementCase`), clean(fields));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },
    updateReturnCase: async (id, fields) => {
        await update(ref(db, `tickets/${id}/returnCase`), clean(fields));
        await update(ref(db, `tickets/${id}`), { updatedAt: Date.now() });
    },

    // --- Audit trail (section 9) — field-level append ---
    addAuditLog: async (id, { action, previousValue, newValue, performedBy, note }) => {
        const aRef = push(ref(db, `tickets/${id}/auditLog`));
        await set(aRef, clean({
            action,
            previousValue: previousValue === undefined ? '' : previousValue,
            newValue: newValue === undefined ? '' : newValue,
            performedBy: performedBy || 'system',
            performedAt: Date.now(),
            note: note || '',
        }));
    },

    // --- Bulk operations (sections 1-4) — all field-level + audited ---
    bulkAssign: async (tickets, { department, agent, note }, currentUser) => {
        const by = currentUser?.uid || currentUser?.email || 'system';
        for (const t of tickets) {
            await update(ref(db, `tickets/${t.id}`), clean({
                assignedDepartment: department || t.assignedDepartment,
                assignedTo: agent || t.assignedTo,
                updatedAt: Date.now(),
            }));
            if (department && department !== t.assignedDepartment) {
                await TicketService.escalate(t.id, { fromDepartment: t.assignedDepartment, toDepartment: department, assignedTo: agent, reason: note || 'Bulk assign' }, currentUser, { ticketNumber: t.ticketNumber, mobile: t.mobile });
            }
            if (note) await TicketService.addInternalNote(t.id, note, currentUser, department || t.assignedDepartment);
            await TicketService.addAuditLog(t.id, { action: 'bulk_assign', previousValue: `${t.assignedDepartment}/${t.assignedTo || '-'}`, newValue: `${department || t.assignedDepartment}/${agent || t.assignedTo || '-'}`, performedBy: by, note });
        }
    },

    bulkClose: async (tickets, { resolutionSummary, notifyTemplateKey }, currentUser) => {
        if (!resolutionSummary || !resolutionSummary.trim()) {
            throw new Error('Resolution summary is required to close tickets.');
        }
        const by = currentUser?.uid || currentUser?.email || 'system';
        for (const t of tickets) {
            await update(ref(db, `tickets/${t.id}`), clean({
                status: 'closed', closedAt: Date.now(), resolutionSummary, closedBy: by, updatedAt: Date.now(),
            }));
            await TicketService.addInternalNote(t.id, `Closed: ${resolutionSummary}`, currentUser, t.assignedDepartment);
            await TicketService.addAuditLog(t.id, { action: 'bulk_close', previousValue: t.status, newValue: 'closed', performedBy: by, note: resolutionSummary });
            if (notifyTemplateKey) {
                const { fillTemplate } = await import('../constants');
                await TicketService.addPublicUpdate(t.id, fillTemplate(notifyTemplateKey, t.ticketNumber), { status: 'closed', channel: 'whatsapp', sentToCustomer: true });
            }
        }
    },

    bulkChange: async (tickets, field, value, currentUser) => {
        const by = currentUser?.uid || currentUser?.email || 'system';
        for (const t of tickets) {
            await update(ref(db, `tickets/${t.id}`), clean({ [field]: value, updatedAt: Date.now() }));
            await TicketService.addAuditLog(t.id, { action: `bulk_${field}`, previousValue: t[field], newValue: value, performedBy: by });
            if (field === 'status') {
                await TicketService.changeStatus(t.id, value, currentUser, { ticketNumber: t.ticketNumber, mobile: t.mobile, issueType: t.issueType });
            }
        }
    },

    bulkAddNote: async (tickets, message, currentUser) => {
        for (const t of tickets) {
            await TicketService.addInternalNote(t.id, message, currentUser, t.assignedDepartment);
            await TicketService.addAuditLog(t.id, { action: 'bulk_note', newValue: message, performedBy: currentUser?.uid || 'system' });
        }
    },

    bulkFollowUp: async (tickets, { followUpAt, assignedTo, reason }, currentUser) => {
        for (const t of tickets) {
            await TicketService.createFollowUp(t, { followUpAt, assignedTo, reason }, currentUser);
            await TicketService.addInternalNote(t.id, `Follow-up scheduled: ${reason || ''}`, currentUser, t.assignedDepartment);
            await TicketService.addAuditLog(t.id, { action: 'bulk_followup', newValue: new Date(followUpAt).toISOString(), performedBy: currentUser?.uid || 'system', note: reason });
        }
    },

    // --- SLA breach manager notification (section 7) — idempotent ---
    notifySlaBreach: async (ticket, currentUser) => {
        if (ticket.slaNotificationSent) return false; // already notified — no spam
        await update(ref(db, `tickets/${ticket.id}`), { slaBreached: true, slaNotificationSent: true, slaNotificationSentAt: Date.now(), updatedAt: Date.now() });
        await TicketService.addInternalNote(ticket.id, `SLA BREACHED — escalated to manager (was due ${new Date(ticket.dueAt).toLocaleString('en-IN')}).`, currentUser, 'manager');
        // Manager To-Do
        const taskRef = push(ref(db, 'tasks'));
        await set(taskRef, clean({
            id: taskRef.key,
            title: `SLA breach: ${ticket.ticketNumber}`,
            ticketId: ticket.id, orderId: ticket.orderId || '', customerName: ticket.customerName || '', phone: ticket.mobile || '',
            dueAt: Date.now(), assignedTo: '', type: 'sla_breach', status: 'pending',
            reason: `Ticket ${ticket.ticketNumber} breached SLA`, createdBy: 'system', createdAt: Date.now(),
        }));
        await TicketService.addAuditLog(ticket.id, { action: 'sla_breach_notified', newValue: 'manager', performedBy: 'system' });
        return true;
    },

    // Post-resolution customer outcome tag. Stored on the ticket AND synced to
    // customerBase/{mobile} so campaigns can target (e.g. win-back the "lost").
    setCustomerOutcome: async (ticket, tag, label, currentUser) => {
        const by = currentUser?.displayName || currentUser?.name || currentUser?.email || 'agent';
        const at = Date.now();
        await update(ref(db, `tickets/${ticket.id}`), clean({
            customerOutcome: { tag, label, by, at }, updatedAt: at,
        }));
        await TicketService.addAuditLog(ticket.id, { action: 'customer_outcome_tagged', newValue: label, performedBy: by });
        const m10 = String(ticket.mobile || '').replace(/\D/g, '').slice(-10);
        if (m10.length === 10) {
            await update(ref(db, `customerBase/${m10}`), clean({
                complaintOutcome: tag,
                complaintOutcomeLabel: label,
                complaintOutcomeAt: at,
                complaintOutcomeTicket: ticket.ticketNumber || '',
                // Campaign/analytics flags follow the tag:
                ...(tag === 'lost' ? { manualLost: true } : {}),
                ...(tag === 'retained' ? { manualLost: false } : {}),
                ...(tag === 'do_not_serve' ? { doNotServe: true, notInterested: true } : {}),
                updatedAt: at,
            }));
        }
    },

    // Merge (section 5)
    mergeTickets: async (newId, oldId) => {
        await update(ref(db, `tickets/${newId}`), { mergedInto: oldId, status: 'closed', updatedAt: Date.now() });
        const oldSnap = await get(ref(db, `tickets/${oldId}/relatedTickets`));
        const related = oldSnap.exists() ? oldSnap.val() : [];
        await update(ref(db, `tickets/${oldId}`), { relatedTickets: [...related, newId], updatedAt: Date.now() });
    },
};

export default TicketService;
